import React, { useEffect, useRef, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import EventIcon from "@mui/icons-material/Event";
import useToggleDropdown from "../../../../../../../../../hooks/useToggleDropdown";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import ActiveFilterIndicator from "./ActiveFilterIndicator";
import { enUS } from "date-fns/locale";
import { STRINGS } from "../../../utils/strings";
import { isValidDate } from "../../../../../../../../ui library/helpers/dates";

export default function DateRange({
  id,
  fieldName,
  title,
  dateFrom,
  dateTo,
  setDateFrom,
  setDateTo,
  dropdown,
  updateFilter,
  isActive,
  setActiveChipLabel,
  bulkUpdateFilters,
}) {
  const [open, setOpen] = useState(false);
  const accordionRef = useRef(null);
  const currentDate = new Date();

  const handleToggle = () => setOpen(!open);

  useToggleDropdown(dropdown, accordionRef, () => setOpen(false));

  useEffect(() => {
    if (isActive === false) {
      handleOnDateChange(setDateFrom, null, "__gte", true);
      handleOnDateChange(setDateTo, null, "__lte", true);
      setActiveChipLabel(id, null);
      bulkUpdateFilters([
        [fieldName + "__gte", undefined],
        [fieldName + "__lte", undefined],
      ]);
    }
    return;
  }, [isActive]);

  const handleOnDateChange = (
    dateSetterFunc,
    newValue,
    filterExtension = "",
    clearOperation = false
  ) => {
    if (!!newValue && !isValidDate(newValue)) return;

    const filterKey = fieldName + filterExtension;
    const filterValue = !newValue
      ? undefined
      : new Date(newValue).toLocaleDateString();
    dateSetterFunc(id, newValue);
    let chipDateFrom = dateFrom
      ? new Date(dateFrom).toLocaleDateString()
      : STRINGS.DATE_START_PLACEHOLDER;
    let chipDateTo = dateTo
      ? new Date(dateTo).toLocaleDateString()
      : currentDate.toLocaleDateString();
    if (filterExtension === "__gte") {
      if (!newValue) {
        chipDateFrom = STRINGS.DATE_START_PLACEHOLDER;
      } else {
        chipDateFrom = new Date(newValue).toLocaleDateString();
      }
    }
    if (filterExtension === "__lte") {
      if (!newValue) {
        chipDateTo = currentDate.toLocaleDateString();
      } else {
        chipDateTo = new Date(newValue).toLocaleDateString();
      }
    }
    if (
      chipDateFrom === STRINGS.DATE_START_PLACEHOLDER &&
      chipDateTo === currentDate.toLocaleDateString()
    ) {
      setActiveChipLabel(id, null);
    } else {
      setActiveChipLabel(id, chipDateFrom + " - " + chipDateTo);
    } 
    if (!clearOperation) {
      updateFilter(filterKey, filterValue);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enUS}>
      <div ref={accordionRef} className="w-full">
        <Accordion expanded={open} onChange={handleToggle} className="w-full">
          <AccordionSummary
            className="text-sm"
            expandIcon={
              <div className="flex items-center">
                <ExpandMoreIcon />
              </div>
            }
            aria-controls="panel1a-content"
            id="panel1a-header"
            slotProps={{ heading: { marginTop: 0 } }}
          >
            <div className="flex flex-row items-center">
              <EventIcon style={{ color: "#757575" }} />
              <span className="ml-2">{title} Range&nbsp;</span>
              {isActive && <ActiveFilterIndicator />}
            </div>
          </AccordionSummary>
          <AccordionDetails>
            <div className="">
              <div className="flex items-center mb-2">
                <DatePicker
                  className="w-full ml-1 my-1 text-center"
                  label={`${title} From`}
                  value={dateFrom}
                  onChange={(newValue) =>
                    handleOnDateChange(setDateFrom, newValue, "__gte")
                  }
                  slotProps={{ textField: { variant: "outlined" } }}
                />
                {!dateFrom ? (
                  ""
                ) : (
                  <div className="p-2">
                    <Button
                      onClick={() =>
                        handleOnDateChange(setDateFrom, null, "__gte")
                      }
                      className="text-sm text-red-600 border-red-600  px-0 hover:bg-red-600 hover:text-white"
                    >
                      X
                    </Button>
                  </div>
                )}
              </div>
              <div className="flex items-center">
                <DatePicker
                  className="w-full ml-1 my-1 text-center"
                  label={`${title} To`}
                  value={dateTo ?? currentDate}
                  onChange={(newValue) =>
                    handleOnDateChange(setDateTo, newValue, "__lte")
                  }
                  slotProps={{ textField: { variant: "outlined" } }}
                />
                {!dateTo ? (
                  ""
                ) : (
                  <div className="p-2">
                    <Button
                      onClick={() =>
                        handleOnDateChange(setDateTo, null, "__lte")
                      }
                      className="text-sm text-red-600 border-red-600  px-0 hover:bg-red-600 hover:text-white"
                    >
                      X
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </AccordionDetails>
        </Accordion>
      </div>
    </LocalizationProvider>
  );
}
